import React, { useEffect, useState } from 'react';
import { Badge } from '../../ui/badge';
import { Separator } from '../../ui/separator';
import { Checkbox } from '../../ui/checkbox';
import { Switch } from '../../ui/switch';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from '../../ui/dialog';
import { formatOrderStatus } from '../../Elements/OrderTimeLine';
import { toast } from '../../../hooks/use-toast';
import { Mail, Save } from 'lucide-react';
import { useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../redux/store';
import { useDispatch } from 'react-redux';
import { doGetCompanies } from '../../../modules/companies/stores/thunk';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import {
  doCreateSetting,
  doDeleteSetting,
  doGetSettings,
  doUpdateSetting,
} from '../../../modules/settings/store/thunk';
import { selectAllSettings } from '../../../modules/settings/store/slice';
import { useTrackEvents } from '../Settings/TrackTrace/TrackEvents.hook';
import { TOKEN_NAME } from '../../../constants';
import Cookies from 'js-cookie';
import { Paywall } from '../../ui/paywall';
import { doContactMe } from '../../../modules/premiumFeatures/stores/thunk';
import { BrandingCustomization } from './CustomBranding';
import { convertModelToFormData } from '../../../utils/common';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../../ui/tooltip';

const ALLOWED_TRACKING_EVENTS = [
  'DRAFT',
  'OUT_FOR_DELIVERY',
  'EXPECTED',
  'AWAITING_NEXT_ATTEMPT',
  'IN_TRANSIT',
  'COMPLETED',
  'RETURNED',
];
const ALLOWED_MESSAGING_CHANNELS = ['EMAIL', 'SMS', 'WHATSAPP'];

const DEFAULT_VALUES = {
  // These values correspond with how default values are set in the backend
  trackingEvents: ['IN_TRANSIT'],
  isTrackingOn: false,
  // hasPro: false,
  language: 'nl',
  messagingChannels: ['EMAIL'],
};

export default function CustomerJourney() {
  const [trackingEvents, setTrackingEvents] = useState<string[]>([...DEFAULT_VALUES.trackingEvents]);
  const [isTrackingOn, setIsTrackingOn] = useState<boolean>(DEFAULT_VALUES.isTrackingOn);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  // const [hasPro, setHasPro] = useState<boolean>(DEFAULT_VALUES.hasPro);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [language, setLanguage] = useState<'en' | 'nl'>(DEFAULT_VALUES.language as 'en' | 'nl');
  const [messagingChannels, setMessagingChannels] = useState<string[]>(['Email']);
  const [testEmail, setTestEmail] = useState<string>('');

  const dispatch = useDispatch<AppDispatch>();
  const isAdmin = useSelector((state: RootState) => state.auth.user.isAdmin);
  const user = useSelector((state: RootState) => state.auth.user);
  const companies = useSelector((state: RootState) => state.company.companies).entities;
  const companyId = useSelector((state: RootState) => state.auth.user.selectedCompany?.id);
  const existing = useSelector((state: RootState) => selectAllSettings(state));
  const [branding, setBranding] = useState<{
    logo: string;
    header: string;
    primaryColor: string;
    secondaryColor: string;
    isOn: boolean;
  }>({
    logo: '',
    header: '',
    primaryColor: '',
    secondaryColor: '',
    isOn: false,
  });

  const { events, initialize } = useTrackEvents(existing);

  // useEffect(() => {
  //   if (isAdmin) {
  //     setHasPro(true); // Allow admins to use the pro features for merchants
  //   }
  // }, [isAdmin]);

  useEffect(() => {
    if (user?.email) {
      setTestEmail(user.email);
    }
  }, [user]);

  // Initialize the settings
  useEffect(() => {
    if (!existing.filter((setting) => setting.keyword === 'ON' && setting.group === 'TNT').length) {
      setIsTrackingOn(DEFAULT_VALUES.isTrackingOn); // Set to default
    } else {
      setIsTrackingOn(
        existing.find((setting) => setting.keyword === 'ON' && setting.group === 'TNT')?.value === 'true',
      );
    }

    if (!existing.filter((setting) => setting.group === 'TNT_EVENTS').length) {
      setTrackingEvents(DEFAULT_VALUES.trackingEvents); // Set to default
    } else {
      if (events) {
        setTrackingEvents(events.filter((event) => event.on === true).map((event) => event.key));
      }
    }

    if (!existing.filter((setting) => setting.group === 'TNT_CHANNELS').length) {
      setMessagingChannels(DEFAULT_VALUES.messagingChannels); // Set to default
    } else {
      setMessagingChannels(
        existing.filter((setting) => setting.group === 'TNT_CHANNELS').map((setting) => setting.keyword),
      );
    }

    if (!existing.filter((setting) => setting.keyword === 'LANGUAGE' && setting.group === 'TNT').length) {
      setLanguage(DEFAULT_VALUES.language as 'en' | 'nl'); // Set to default
    } else {
      setLanguage(
        existing.find((setting) => setting.keyword === 'LANGUAGE' && setting.group === 'TNT')?.value as 'en' | 'nl',
      );
    }

    const brandingSettings = existing.filter((setting) => setting.group === 'TNT_BRANDING');
    if (brandingSettings) {
      const existingBrandingSetting = {
        logo: brandingSettings.find((setting) => setting.keyword === 'LOGO')?.value || '',
        header: brandingSettings.find((setting) => setting.keyword === 'HEADER')?.value || '',
        primaryColor: brandingSettings.find((setting) => setting.keyword === 'PRIMARY_COLOR')?.value || '',
        secondaryColor: brandingSettings.find((setting) => setting.keyword === 'SECONDARY_COLOR')?.value || '',
        isOn: brandingSettings.find((setting) => setting.keyword === 'ON')?.value === 'true',
      };
      setBranding(existingBrandingSetting);
    }
  }, [existing, events]);

  useEffect(() => {
    if (existing) {
      initialize(existing);
    }
  }, [existing]);

  useEffect(() => {
    if (companyId) {
      setSelectedCompanyId(companyId);
    }
  }, [companyId]);

  useEffect(() => {
    if (selectedCompanyId) {
      dispatch(doGetSettings({ category: 'tnt-settings', companyId: selectedCompanyId }));
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    const fetchCompanies = async () => {
      if (isAdmin) {
        await dispatch(doGetCompanies({ page: 1, take: 1000 }));
      }
    };
    fetchCompanies();
  }, [isAdmin]);

  const handleTrackingEventsChange = (checked: boolean, status: string) => {
    if (checked) {
      setTrackingEvents([...trackingEvents, status]);
    } else {
      setTrackingEvents(trackingEvents.filter((event) => event !== status));
    }
  };

  const handleMessagingChannelsChange = (checked: boolean, channel: string) => {
    if (checked) {
      setMessagingChannels([...messagingChannels, channel]);
    } else {
      setMessagingChannels(messagingChannels.filter((c) => c !== channel));
    }
  };

  const handleClick = (checked: boolean, statusOrChannel: string) => {
    if (!user.fullyTracked) {
      setDialogOpen(true);
      return;
    }
    if (ALLOWED_TRACKING_EVENTS.includes(statusOrChannel)) {
      handleTrackingEventsChange(checked, statusOrChannel);
    } else {
      handleMessagingChannelsChange(checked, statusOrChannel);
    }
  };

  const sendTestEmails = async (recipient: string) => {
    if (!recipient) {
      toast({
        title: 'No recipient email provided',
        description: 'Please enter an email address to send the test email to',
        variant: 'destructive',
      });
      return;
    }
    if (trackingEvents.length === 0) {
      toast({
        title: 'No tracking events selected',
        description: 'Please select at least one tracking event',
        variant: 'destructive',
      });
      return;
    } else {
      try {
        await fetch(`${process.env.REACT_APP_API_URI}/tracking/test-sequence`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${Cookies.get(TOKEN_NAME)}`,
          },
          body: JSON.stringify({
            userId: user.id,
            trackingEvents,
            language,
            recipient,
          }),
        });
        toast({
          title: 'Test emails sent successfully',
          description: 'The test emails have been sent successfully',
          variant: 'default',
        });
      } catch (error) {
        console.error('Error sending test emails:', error);
        toast({
          title: 'Error sending test emails',
          description: 'There was a problem sending the test emails. Please try again.',
          variant: 'destructive',
        });
      }
    }
  };

  const handleSave = async () => {
    const trackingONUpdates = {
      id: existing.find((e) => e.keyword === 'ON')?.id || null,
      keyword: 'ON',
      value: isTrackingOn ? 'true' : 'false',
      group: 'TNT',
      companyId: selectedCompanyId,
    };

    const trackingEventsUpdates = events.map((s) => ({
      id: existing.find((e) => s.key === e.keyword)?.id || null,
      keyword: s.key,
      value: trackingEvents.includes(s.key) ? 'true' : 'false',
      group: 'TNT_EVENTS',
      companyId: selectedCompanyId,
    }));

    const languageUpdates = {
      id: existing.find((e) => e.keyword === 'LANGUAGE')?.id || null,
      keyword: 'LANGUAGE',
      value: language,
      group: 'TNT',
      companyId: selectedCompanyId,
    };

    const messagingChannelsUpdates = messagingChannels.map((s) => ({
      id: existing.find((e) => s === e.keyword)?.id || null,
      keyword: s,
      value: 'true',
      group: 'TNT_CHANNELS',
      companyId: selectedCompanyId,
    }));

    const updates = [trackingONUpdates, ...trackingEventsUpdates, languageUpdates, ...messagingChannelsUpdates];

    try {
      await Promise.all(
        updates.map(async (u) => {
          if (u.id && u.value) {
            // if it existed and has a value => update it
            await dispatch(doUpdateSetting(u));
          } else if (u.id && !u.value) {
            // if it existed and does not have a value => delete it
            await dispatch(doDeleteSetting(u));
          } else if (u.value) {
            // if it didn't exist => create it
            await dispatch(doCreateSetting(u));
          }
        }),
      );
      toast({
        title: 'Settings saved successfully',
        description: 'Your customer journey settings have been updated.',
        variant: 'default',
      });
      if (selectedCompanyId) {
        dispatch(doGetSettings({ category: 'tnt-settings', companyId: selectedCompanyId }));
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Error saving settings',
        description: 'There was a problem saving your settings. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleBrandingSave = async (branding: {
    logo: string;
    header: string;
    primaryColor: string;
    secondaryColor: string;
    isOn: boolean;
    logoFile: File | null;
    headerFile: File | null;
  }) => {
    const brandingOnUpdates = {
      id: existing.find((e) => e.keyword === 'ON' && e.group === 'TNT_BRANDING')?.id || null,
      keyword: 'ON',
      value: branding.isOn ? 'true' : 'false',
      group: 'TNT_BRANDING',
      companyId: selectedCompanyId,
    };

    const brandingLogoUpdates = {
      id: existing.find((e) => e.keyword === 'LOGO' && e.group === 'TNT_BRANDING')?.id || null,
      keyword: 'LOGO',
      value: '',
      group: 'TNT_BRANDING',
      companyId: selectedCompanyId,
    };

    const brandingHeaderUpdates = {
      id: existing.find((e) => e.keyword === 'HEADER' && e.group === 'TNT_BRANDING')?.id || null,
      keyword: 'HEADER',
      value: '',
      group: 'TNT_BRANDING',
      companyId: selectedCompanyId,
    };

    const brandingPrimaryColorUpdates = {
      id: existing.find((e) => e.keyword === 'PRIMARY_COLOR' && e.group === 'TNT_BRANDING')?.id || null,
      keyword: 'PRIMARY_COLOR',
      value: branding.primaryColor,
      group: 'TNT_BRANDING',
      companyId: selectedCompanyId,
    };

    const brandingSecondaryColorUpdates = {
      id: existing.find((e) => e.keyword === 'SECONDARY_COLOR' && e.group === 'TNT_BRANDING')?.id || null,
      keyword: 'SECONDARY_COLOR',
      value: branding.secondaryColor,
      group: 'TNT_BRANDING',
      companyId: selectedCompanyId,
    };

    const updates = [
      brandingOnUpdates,
      brandingLogoUpdates,
      brandingHeaderUpdates,
      brandingPrimaryColorUpdates,
      brandingSecondaryColorUpdates,
    ];

    try {
      await Promise.all(
        updates.map(async (u) => {
          if (!u.id && u.keyword === 'LOGO' && branding.logoFile) {
            const formData = convertModelToFormData(u);
            formData.append('file', branding.logoFile);
            await dispatch(doCreateSetting(formData));
          } else if (!u.id && u.keyword === 'HEADER' && branding.headerFile) {
            const formData = convertModelToFormData(u);
            formData.append('file', branding.headerFile);
            await dispatch(doCreateSetting(formData));
          } else if (u.id && u.value) {
            await dispatch(doUpdateSetting(u));
          } else if (u.id && u.keyword === 'LOGO' && branding.logoFile) {
            const formData = convertModelToFormData(u);
            formData.append('file', branding.logoFile);
            await dispatch(doUpdateSetting(formData));
          } else if (u.id && u.keyword === 'HEADER' && branding.headerFile) {
            const formData = convertModelToFormData(u);
            formData.append('file', branding.headerFile);
            await dispatch(doUpdateSetting(formData));
          } else if (u.id && !u.value) {
            if (u.keyword === 'LOGO' && branding.logo) {
              return;
            } else if (u.keyword === 'HEADER' && branding.header) {
              return;
            } else {
              await dispatch(doDeleteSetting(u));
            }
          } else if (u.value) {
            await dispatch(doCreateSetting(u));
          }
        }),
      );
      toast({
        title: 'Branding saved successfully',
        description: 'Your branding settings have been updated.',
        variant: 'default',
      });
      if (selectedCompanyId) {
        dispatch(doGetSettings({ category: 'tnt-settings', companyId: selectedCompanyId }));
      }
    } catch (error) {
      console.error('Error saving branding:', error);
      toast({
        title: 'Error saving branding',
        description: 'There was a problem saving your branding settings. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const customerJourneyDescriptions = [
    'Give your customers a taylored experience',
    'Apply branded communications and customize your wording',
    'Increase your conversion',
  ];
  const contactMe = async () => {
    const params = { feature: 'PERFORMANCE_DASHBOARD' };
    try {
      await dispatch(doContactMe(params));
    } catch (e) {
      //
    }
  };

  return (
    <>
      {!user.fullyTracked && (
        <Paywall
          title="Customer Journey"
          descriptions={customerJourneyDescriptions}
          previewable={false}
          contactMe={contactMe}
          startPreview={() => null}
        />
      )}
      <div className="bg-white min-h-screen">
        <div className="flex justify-center">
          <div className="w-full max-w-screen-xl bg-white">
            {/* Top Bar */}
            <div className="flex justify-between items-center">
              <div className="text-left">
                <h1 className="text-2xl font-semibold">Customer Journey</h1>
                <p className="text-sm text-muted-foreground">
                  Customise your post purchase experience with custom branding and messaging
                </p>
              </div>
              {isAdmin && companies && Object.keys(companies).length > 0 && (
                <div className="w-40">
                  <Select onValueChange={(value) => setSelectedCompanyId(value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a company" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(companies).map((company: { id: string; name: string }) => (
                        <SelectItem key={company.id} value={company.id} title={company.name}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <Separator className="mt-4" />

            {/* Settings UI */}
            <div className="m-8 flex flex-col gap-8">
              {/* Track & Trace */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-semibold">Track & Trace</h2>
                  <p className="text-sm text-muted-foreground">
                    Enable or disable track & trace updates for your customers
                  </p>
                </div>
                <div className="flex items-center gap-2 mt-8">
                  <span className="text-sm">Off</span>
                  <Switch onCheckedChange={(checked) => setIsTrackingOn(checked)} checked={isTrackingOn} />
                  <span className="text-sm">On</span>
                </div>
              </div>

              {/* Language */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-semibold">Messaging Language</h2>
                  <p className="text-sm text-muted-foreground">
                    Choose the language you want to use for your messaging
                  </p>
                </div>
                <div className="flex items-center gap-2 mt-8">
                  <Button
                    variant={'outline'}
                    className={language === 'en' ? 'border-black' : ''}
                    onClick={() => setLanguage('en')}
                    disabled={!isTrackingOn}
                  >
                    🇬🇧 English
                  </Button>
                  <Button
                    variant={'outline'}
                    className={language === 'nl' ? 'border-black' : ''}
                    onClick={() => setLanguage('nl')}
                    disabled={!isTrackingOn}
                  >
                    🇳🇱 Dutch
                  </Button>
                </div>
              </div>

              {/* Tracking Events */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-semibold">Tracking Events</h2>
                    <Badge variant="outline" className="ml-2 px-2 py-0.5 text-xs">
                      Pro
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Choose which status updates you want to update your customers about
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-8">
                  {ALLOWED_TRACKING_EVENTS.map((event, index) => {
                    return (
                      <label className="flex items-center space-x-2" key={event}>
                        <Checkbox
                          key={index}
                          onCheckedChange={(checked) => handleClick(checked as boolean, event)}
                          checked={trackingEvents.includes(event)}
                          disabled={!isTrackingOn}
                        />
                        <span key={event}>{formatOrderStatus(event)}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Messaging Channels */}
            <div className="m-8 flex items-center justify-between">
              <div>
                <h2 className="font-semibold flex items-center">
                  Messaging Channels
                  <Badge variant="outline" className="ml-2 px-2 py-0.5 text-xs">
                    Pro
                  </Badge>
                </h2>
                <p className="text-sm text-muted-foreground">Select your preferred messaging channels</p>
              </div>
              <div className="grid grid-cols-1 gap-2 mt-8">
                {ALLOWED_MESSAGING_CHANNELS.map((channel, index) => {
                  return (
                    <label className="flex items-center space-x-2" key={channel}>
                      <Checkbox
                        disabled={!isTrackingOn}
                        onCheckedChange={(checked) => handleClick(checked as boolean, channel)}
                        checked={messagingChannels.includes(channel)}
                        key={index}
                      />
                      <span key={channel}>{channel.charAt(0) + channel.slice(1).toLowerCase()}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end m-8 gap-4">
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="px-8"
                      variant="outline"
                      disabled={!isTrackingOn}
                      onClick={() => sendTestEmails(testEmail)}
                    >
                      <Mail />
                      Send Test E-mails
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="w-80 border border-gray-200 bg-white shadow-md">
                    <div className="grid gap-4 p-2">
                      <div className="grid gap-2">
                        <Label htmlFor="email" className="text-sm font-semibold text-gray-900">
                          Send test email to
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="Enter email address"
                          value={testEmail}
                          onChange={(e) => setTestEmail(e.target.value)}
                          className="placeholder:text-gray-400 text-gray-800"
                        />
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button className="px-8" onClick={handleSave} disabled={!selectedCompanyId}>
                <Save /> Save
              </Button>
            </div>

            <Separator className="mt-4" />

            <div className="m-8 flex flex-col gap-8">
              <div className="flex justify-between items-center">
                <div className="text-left">
                  <div className="flex items-center gap-2">
                    <h1 className="text-xl font-semibold">Custom branding</h1>
                    <Badge variant="outline" className="ml-2 px-2 py-0.5 text-xs">
                      Pro
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Customise the look and feel of the messaging and tracking page
                  </p>
                </div>
              </div>
              <div>
                <BrandingCustomization
                  onSave={async (branding) => {
                    await handleBrandingSave(branding);
                  }}
                  existing={branding}
                  selectedCompanyId={selectedCompanyId}
                />
                {/* Track & Trace
              <div className="flex items-center justify-between">
                <div className="w-full p-6 border rounded-lg shadow-sm">
                  <div className="flex flex-col gap-4">
                    <div>
                      <h2 className="font-semibold">Elevate your brand experience with Sparqle Pro</h2>
                      <p className="text-sm text-muted-foreground">
                        Turn your post-purchase journey into a branded moment your customers will remember. Customize
                        your tracking page and notifications with your logo, colors, and tone of voice - and increase
                        repeat purchases and customer satisfaction. Want to see how other brands boosted loyalty with
                        branded tracking? Click <strong>Learn more</strong> — we will get in touch and show you what is
                        possible.
                      </p>
                    </div>
                    <div className="flex items-center justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          contactMe();
                          toast({
                            title: 'Noted!',
                            description: 'We will contact you about custom branding',
                          });
                        }}
                      >
                        <Info />
                        Learn More
                      </Button>
                    </div>
                  </div>
                </div> */}
              </div>
            </div>
          </div>
          {/* Dialog for Pro feature */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent>
              <DialogTitle>Pro Feature</DialogTitle>
              <DialogDescription>
                This feature is available for Pro users only. Upgrade to Pro to enable custom tracking events.
              </DialogDescription>
              <DialogFooter>
                <Button onClick={() => setDialogOpen(false)}>Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </>
  );
}
